import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
    PieChart, Pie, Cell, Tooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    ResponsiveContainer, LabelList,
} from "recharts";

// グラフの色パレット（最大10色）
const COLORS = [
    "#1e40af", "#059669", "#dc2626", "#d97706", "#7c3aed",
    "#0891b2", "#be185d", "#15803d", "#c2410c", "#4338ca",
];

const pct = (n, total) =>
    total === 0 ? "0%" : `${((n / total) * 100).toFixed(1)}%`;

// 円グラフのラベル（外側に%表示）
const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (percent < 0.04) return null; // 4%未満は省略
    return (
        <text
            x={x} y={y}
            fill="#374151"
            textAnchor={x > cx ? "start" : "end"}
            dominantBaseline="central"
            fontSize={12}
            fontWeight={600}
        >
            {`${(percent * 100).toFixed(1)}%`}
        </text>
    );
};

// 棒グラフのカスタムラベル（バー内に数値表示）
const BarValueLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (!value || height < 14) return null;
    return (
        <text
            x={x + width / 2} y={y + height / 2}
            fill="#fff" textAnchor="middle" dominantBaseline="central"
            fontSize={13} fontWeight={700}
        >
            {value}
        </text>
    );
};

/**
 * アンケート集計画面（管理側）
 * - 棒グラフ・円グラフ・回答数・回答率を表示
 */
const EventAnketoStatistics = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [responses, setResponses] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [eventName, setEventName] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState({}); // { qIndex: "bar" | "pie" }

    const fetchData = React.useCallback(async (isManualRefresh = false) => {
        if (isManualRefresh) setRefreshing(true);
        else setLoading(true);
        setError("");
        const t = Date.now();
        try {
            const [resData, eventRes] = await Promise.all([
                api.get(`/events/${eventId}/get_responses/?_t=${t}`),
                api.get(`/events/${eventId}/?_t=${t}`),
            ]);
            setResponses(Array.isArray(resData.data) ? resData.data : []);
            setQuestions(eventRes.data.anketo_questions || []);
            setEventName(eventRes.data.eventName || "");
        } catch (err) {
            setError("データの取得に失敗しました。");
            console.error(err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchData();
        const timer = setInterval(() => fetchData(), 60000);
        return () => clearInterval(timer);
    }, [fetchData]);

    // 各質問ごとの集計データ
    const statsByQuestion = useMemo(() => {
        const qList = Array.isArray(questions) ? questions : [];
        return qList.map((q, qi) => {
            const isObj = q && typeof q === "object";
            const text = isObj
                ? String(q.question || q.q || q.text || `質問 ${qi + 1}`)
                : String(q || `質問 ${qi + 1}`);
            const options = isObj && Array.isArray(q.options) ? q.options : null;

            const counts = {};
            let empty = 0;
            for (const r of responses) {
                const ans = r.answers;
                let val = null;
                if (Array.isArray(ans)) val = ans[qi];
                else if (ans && typeof ans === "object") val = ans[qi] ?? ans[String(qi)];
                const s = val != null && String(val).trim() !== "" ? String(val).trim() : "";
                if (!s) { empty++; continue; }
                counts[s] = (counts[s] || 0) + 1;
            }

            // 選択肢の順序を維持（options がある場合）
            let entries;
            if (options && options.length > 0) {
                entries = options
                    .filter(opt => counts[opt] !== undefined || true)
                    .map(opt => [opt, counts[opt] || 0]);
            } else {
                entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
            }

            const answered = responses.length - empty;
            return { qi, text, entries, empty, answered, total: responses.length, options };
        });
    }, [questions, responses]);

    const getTab = (qi) => activeTab[qi] || "bar";
    const setTab = (qi, tab) => setActiveTab(prev => ({ ...prev, [qi]: tab }));

    if (loading) {
        return <div className="container mt-4"><p>読み込み中...</p></div>;
    }

    const totalRespondents = responses.length;
    const totalQuestions = questions.length;

    return (
        <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "40px 24px 60px", boxSizing: "border-box" }}>
            <div style={{ maxWidth: 960, margin: "0 auto" }}>

                {/* ヘッダー */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
                    <button
                        type="button"
                        onClick={() => navigate("/event/anketo")}
                        style={{
                            background: "#6b7280", color: "#fff", border: "none",
                            borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                            fontSize: "0.9rem", cursor: "pointer", whiteSpace: "nowrap",
                        }}
                    >
                        ← 前画面に戻る
                    </button>
                    <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "#111827", flex: 1 }}>
                        アンケート集計{eventName ? `：${eventName}` : ""}
                    </h3>
                    <button
                        type="button"
                        onClick={() => navigate(`/event/${eventId}/ai-report`)}
                        style={{
                            background: "#7c3aed", color: "#fff", border: "none",
                            borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                            fontSize: "0.9rem", cursor: "pointer",
                        }}
                    >
                        ✨ AI分析レポートを作成
                    </button>
                    <button
                        type="button"
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                        style={{
                            background: refreshing ? "#9ca3af" : "#2563eb", color: "#fff", border: "none",
                            borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                            fontSize: "0.9rem", cursor: refreshing ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                        }}
                    >
                        {refreshing ? "更新中..." : "🔄 更新"}
                    </button>
                </div>

                {error && <div style={{ background: "#fee2e2", color: "#991b1b", padding: "12px 16px", borderRadius: 8, marginBottom: 20 }}>{error}</div>}

                {/* サマリーカード */}
                <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 16, marginBottom: 28,
                }}>
                    {[
                        { label: "総回答者数", value: `${totalRespondents} 人`, color: "#1e40af" },
                        { label: "質問数", value: `${totalQuestions} 問`, color: "#059669" },
                    ].map(({ label, value, color }) => (
                        <div key={label} style={{
                            background: "#fff", borderRadius: 12, padding: "18px 24px",
                            border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                            textAlign: "center",
                        }}>
                            <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>{label}</div>
                            <div style={{ fontSize: "1.8rem", fontWeight: 800, color }}>{value}</div>
                        </div>
                    ))}
                </div>

                {totalRespondents === 0 ? (
                    <div style={{
                        background: "#fff", borderRadius: 12, padding: "48px 24px",
                        textAlign: "center", color: "#6b7280",
                        border: "1px solid #e5e7eb",
                    }}>
                        まだ回答がありません。
                    </div>
                ) : (
                    statsByQuestion.map((row) => {
                        const chartData = row.entries.map(([label, n], i) => ({
                            name: label,
                            count: n,
                            pct: row.answered === 0 ? 0 : parseFloat(((n / row.answered) * 100).toFixed(1)),
                            color: COLORS[i % COLORS.length],
                        }));
                        const tab = getTab(row.qi);

                        return (
                            <div key={row.qi} style={{
                                background: "#fff", borderRadius: 12, padding: "24px 28px",
                                marginBottom: 28, border: "1px solid #e5e7eb",
                                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                            }}>
                                {/* 質問ヘッダー */}
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                                    <span style={{
                                        background: "#1e40af", color: "#fff",
                                        borderRadius: 999, padding: "2px 12px",
                                        fontSize: "0.82rem", fontWeight: 800,
                                    }}>
                                        Q{row.qi + 1}
                                    </span>
                                    <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#111827", flex: 1 }}>
                                        {row.text}
                                    </h4>
                                </div>
                                <div style={{ fontSize: "0.83rem", color: "#6b7280", marginBottom: 16 }}>
                                    回答数：{row.answered} / {row.total}人　　未回答：{row.empty}人
                                    {row.total > 0 && (
                                        <span style={{ marginLeft: 12, fontWeight: 600, color: "#1e40af" }}>
                                            回答率：{pct(row.answered, row.total)}
                                        </span>
                                    )}
                                </div>

                                {/* グラフ切り替えタブ */}
                                <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                                    {["bar", "pie"].map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => setTab(row.qi, t)}
                                            style={{
                                                padding: "5px 16px", borderRadius: 6, border: "none",
                                                fontWeight: 600, fontSize: "0.83rem", cursor: "pointer",
                                                background: tab === t ? "#1e40af" : "#e5e7eb",
                                                color: tab === t ? "#fff" : "#374151",
                                            }}
                                        >
                                            {t === "bar" ? "📊 棒グラフ" : "🥧 円グラフ"}
                                        </button>
                                    ))}
                                </div>

                                {/* === 棒グラフ === */}
                                {tab === "bar" && (
                                    <div>
                                        <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 52 + 40)}>
                                            <BarChart
                                                data={chartData}
                                                layout="vertical"
                                                margin={{ top: 4, right: 80, left: 8, bottom: 4 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis
                                                    type="number"
                                                    domain={[0, row.answered || 1]}
                                                    allowDecimals={false}
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="name"
                                                    width={160}
                                                    tick={{ fontSize: 12, fill: "#374151" }}
                                                />
                                                <Tooltip
                                                    formatter={(value, name, props) => [
                                                        `${value}人 (${props.payload.pct}%)`,
                                                        "回答数",
                                                    ]}
                                                />
                                                <Bar dataKey="count" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                                                    {chartData.map((entry, i) => (
                                                        <Cell key={i} fill={entry.color} />
                                                    ))}
                                                    <LabelList
                                                        dataKey="count"
                                                        content={BarValueLabel}
                                                    />
                                                    {/* バーの外側に%表示 */}
                                                    <LabelList
                                                        dataKey="pct"
                                                        position="right"
                                                        formatter={(v) => `${v}%`}
                                                        style={{ fontSize: 12, fontWeight: 700, fill: "#374151" }}
                                                    />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}

                                {/* === 円グラフ === */}
                                {tab === "pie" && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                                        <div style={{ flex: "1 1 300px", minWidth: 280 }}>
                                            <ResponsiveContainer width="100%" height={280}>
                                                <PieChart>
                                                    <Pie
                                                        data={chartData}
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={100}
                                                        dataKey="count"
                                                        labelLine={false}
                                                        label={renderPieLabel}
                                                        isAnimationActive={false}
                                                    >
                                                        {chartData.map((entry, i) => (
                                                            <Cell key={i} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip
                                                        formatter={(value, name, props) => [
                                                            `${value}人 (${props.payload.pct}%)`,
                                                            name,
                                                        ]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* 凡例 */}
                                        <div style={{ flex: "1 1 180px" }}>
                                            {chartData.map((d, i) => (
                                                <div key={i} style={{
                                                    display: "flex", alignItems: "center", gap: 8,
                                                    marginBottom: 8,
                                                }}>
                                                    <span style={{
                                                        width: 14, height: 14, borderRadius: 3,
                                                        background: d.color, flexShrink: 0,
                                                    }} />
                                                    <span style={{ fontSize: "0.87rem", color: "#374151", flex: 1 }}>
                                                        {d.name}
                                                    </span>
                                                    <span style={{ fontSize: "0.87rem", fontWeight: 700, color: "#1e40af", whiteSpace: "nowrap" }}>
                                                        {d.count}人 ({d.pct}%)
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 集計テーブル（棒グラフ・円グラフ共通） */}
                                {/*}
                                <div style={{ marginTop: 20, overflowX: "auto" }}>
                                    <table style={{
                                        width: "100%", borderCollapse: "collapse",
                                        fontSize: "0.9rem",
                                    }}>
                                        <thead>
                                            <tr style={{ background: "#1e3a5f", color: "#fff" }}>
                                                <th style={{ textAlign: "left", padding: "8px 14px", borderRadius: "6px 0 0 0" }}>回答内容</th>
                                                <th style={{ textAlign: "right", padding: "8px 14px", width: 80 }}>件数</th>
                                                <th style={{ textAlign: "right", padding: "8px 14px", width: 90, borderRadius: "0 6px 0 0" }}>割合</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chartData.map((d, i) => (
                                                <tr key={i} style={{
                                                    background: i % 2 === 0 ? "#fff" : "#f9fafb",
                                                    borderBottom: "1px solid #e5e7eb",
                                                }}>
                                                    <td style={{ padding: "9px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                                                        <span style={{
                                                            width: 10, height: 10, borderRadius: 2,
                                                            background: d.color, flexShrink: 0, display: "inline-block",
                                                        }} />
                                                        {d.name}
                                                    </td>
                                                    <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 700 }}>{d.count}人</td>
                                                    <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 700, color: "#1e40af" }}>{d.pct}%</td>
                                                </tr>
                                            ))}
                                            <tr style={{ background: "#f0f4ff", fontWeight: 700, borderTop: "2px solid #bfdbfe" }}>
                                                <td style={{ padding: "9px 14px" }}>合計（回答あり）</td>
                                                <td style={{ padding: "9px 14px", textAlign: "right" }}>{row.answered}人</td>
                                                <td style={{ padding: "9px 14px", textAlign: "right", color: "#1e40af" }}>100%</td>
                                            </tr>
                                            {row.empty > 0 && (
                                                <tr style={{ background: "#fafafa", color: "#9ca3af" }}>
                                                    <td style={{ padding: "9px 14px" }}>未回答</td>
                                                    <td style={{ padding: "9px 14px", textAlign: "right" }}>{row.empty}人</td>
                                                    <td style={{ padding: "9px 14px", textAlign: "right" }}>—</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                */}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default EventAnketoStatistics;
